/**
 * Tracking utilities for analytics and campaign tracking
 * Copyright (c) 2025 NoorWays. All rights reserved.
 */

/**
 * Base Chrome Web Store URL for NoorWriter extension
 */
const CHROME_STORE_BASE_URL = 'https://chromewebstore.google.com/detail/fjlfoaokggeemlcoemkhokccampibipm'

/**
 * UTM parameter configuration for tracking Chrome Web Store links
 */
interface UTMParams {
  /** Traffic source (e.g., 'noorwriter_web') */
  source?: string
  /** Marketing medium (e.g., 'website', 'email') */
  medium?: string
  /** Campaign name (e.g., 'extension_landing_page', 'main_navigation') */
  campaign?: string
  /** Content identifier for A/B testing (e.g., 'hero_cta', 'footer_link') */
  content?: string
  /** Paid search keywords (rarely used for extension links) */
  term?: string
}

/**
 * Predefined campaign identifiers for common use cases
 */
export const CAMPAIGNS = {
  EXTENSION_LANDING: 'extension_landing_page',
  MAIN_NAVIGATION: 'main_navigation',
  FOOTER: 'footer',
  PRICING_PAGE: 'pricing_page',
  SUPPORT_PAGE: 'support_page',
  HOME_PAGE: 'home_page',
} as const

/**
 * Predefined content identifiers for common link locations
 */
export const CONTENT_IDS = {
  HERO_CTA: 'hero_cta',
  SECONDARY_CTA: 'secondary_cta',
  FINAL_CTA: 'final_cta',
  NAV_EXTENSION_LINK: 'nav_extension_link',
  FOOTER_EXTENSION_LINK: 'footer_extension_link',
  PRICING_CTA: 'pricing_cta',
  SUPPORT_CTA: 'support_cta',
} as const

/**
 * Generate a Chrome Web Store URL with UTM tracking parameters
 *
 * @param params - UTM parameters for tracking
 * @returns Complete Chrome Web Store URL with UTM parameters
 *
 * @example
 * ```typescript
 * // Extension landing page hero CTA
 * const url = getChromeStoreUrl({
 *   campaign: CAMPAIGNS.EXTENSION_LANDING,
 *   content: CONTENT_IDS.HERO_CTA
 * })
 * // Returns: https://chromewebstore.google.com/detail/...?utm_source=noorwriter_web&utm_medium=website&utm_campaign=extension_landing_page&utm_content=hero_cta
 *
 * // Navigation link
 * const navUrl = getChromeStoreUrl({
 *   campaign: CAMPAIGNS.MAIN_NAVIGATION,
 *   content: CONTENT_IDS.NAV_EXTENSION_LINK
 * })
 * ```
 */
export function getChromeStoreUrl(params: UTMParams = {}): string {
  const {
    source = 'noorwriter_web',
    medium = 'website',
    campaign = CAMPAIGNS.EXTENSION_LANDING,
    content,
    term,
  } = params

  const url = new URL(CHROME_STORE_BASE_URL)

  // Add UTM parameters
  url.searchParams.set('utm_source', source)
  url.searchParams.set('utm_medium', medium)
  url.searchParams.set('utm_campaign', campaign)

  if (content) {
    url.searchParams.set('utm_content', content)
  }

  if (term) {
    url.searchParams.set('utm_term', term)
  }

  return url.toString()
}

/**
 * Track a custom event (placeholder for future analytics integration)
 *
 * @param eventName - Name of the event to track
 * @param eventData - Additional data to send with the event
 *
 * @example
 * ```typescript
 * trackEvent('extension_cta_clicked', {
 *   location: 'hero',
 *   campaign: 'extension_landing_page'
 * })
 * ```
 */
export function trackEvent(eventName: string, eventData?: Record<string, unknown>): void {
  // TODO: Integrate with analytics service (Google Analytics, Application Insights, etc.)
  if (import.meta.env.DEV) {
    console.log('[Analytics]', eventName, eventData)
  }

  // Future implementation:
  // - Send to Google Analytics
  // - Send to Application Insights
  // - Send to custom analytics endpoint
}

/**
 * Track a page view (placeholder for future analytics integration)
 *
 * @param pagePath - Path of the page being viewed
 * @param pageTitle - Title of the page
 *
 * @example
 * ```typescript
 * trackPageView('/extension', 'Chrome Extension')
 * ```
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  // TODO: Integrate with analytics service
  if (import.meta.env.DEV) {
    console.log('[Analytics] Page View:', pagePath, pageTitle)
  }

  // Future implementation:
  // - Send to Google Analytics
  // - Send to Application Insights
}

/**
 * Track an outbound link click
 *
 * @param url - URL being clicked
 * @param linkText - Text of the link
 *
 * @example
 * ```typescript
 * trackOutboundLink('https://chromewebstore.google.com/...', 'Add to Chrome')
 * ```
 */
export function trackOutboundLink(url: string, linkText?: string): void {
  trackEvent('outbound_link_click', {
    url,
    linkText,
    timestamp: new Date().toISOString(),
  })
}

/**
 * Track Chrome Web Store CTA click
 *
 * @param location - Location of the CTA (e.g., 'hero', 'footer', 'navigation')
 * @param campaign - Campaign identifier
 *
 * @example
 * ```typescript
 * trackChromeStoreCTA('hero', CAMPAIGNS.EXTENSION_LANDING)
 * ```
 */
export function trackChromeStoreCTA(location: string, campaign: string): void {
  trackEvent('chrome_store_cta_clicked', {
    location,
    campaign,
    timestamp: new Date().toISOString(),
  })
}

